import React from 'react';
import { Plus, Trash2, Receipt } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const ReservaForm = ({
  open,
  onClose,
  formData,
  setFormData,
  reservaEditando,
  clientes,
  canales,
  estadosReserva,
  categorias,
  proveedores,
  clienteSeleccionado,
  onSeleccionarCliente,
  onAbrirNuevoCliente,
  gastosReserva,
  onAgregarGasto,
  onActualizarGasto,
  onEliminarGasto,
  onGuardar,
  obtenerProveedoresPorCategoria
}) => {
  const calcularNoches = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 0;
    const inicio = new Date(checkIn);
    const fin = new Date(checkOut);
    const diffTime = Math.abs(fin - inicio);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const calcularTotalGastos = () => {
    return gastosReserva.reduce((total, gasto) => total + (parseFloat(gasto.monto) || 0), 0);
  };

  // Sub Total = noches × valor_noche
  const calcularSubTotal = (noches, valorNoche) => {
    return noches * valorNoche;
  };

  // Monto Total = Sub Total + Gastos Asociados
  const calcularMontoTotal = (subTotal, totalGastos) => {
    return subTotal + totalGastos;
  };

  const calcularComision = (subTotal, canal) => {
    const canalNombre = canales.find(c => c.id_canal === canal)?.nombre || '';
    if (canalNombre.toLowerCase() === 'airbnb') return subTotal * 0.15;
    if (canalNombre.toLowerCase() === 'booking') return subTotal * 0.15;
    return 0;
  };

  const actualizarMontos = (noches, valorNoche) => {
    const subTotal = calcularSubTotal(noches, valorNoche);
    const comision = calcularComision(subTotal, formData.canal_id);
    return { monto_bruto: subTotal, comisiones: comision };
  };

  const handleCheckInChange = (value) => {
    const noches = calcularNoches(value, formData.fecha_check_out);
    const { monto_bruto, comisiones } = actualizarMontos(noches, formData.valor_noche || 0);
    setFormData({ ...formData, fecha_check_in: value, noches, monto_bruto, comisiones });
  };

  const handleCheckOutChange = (value) => {
    const noches = calcularNoches(formData.fecha_check_in, value);
    const { monto_bruto, comisiones } = actualizarMontos(noches, formData.valor_noche || 0);
    setFormData({ ...formData, fecha_check_out: value, noches, monto_bruto, comisiones });
  };

  const handleValorNocheChange = (value) => {
    const valorNoche = parseFloat(value) || 0;
    const noches = calcularNoches(formData.fecha_check_in, formData.fecha_check_out);
    const subTotal = noches > 0 ? noches * valorNoche : 0;
    const comision = calcularComision(subTotal, formData.canal_id);
    setFormData({ ...formData, valor_noche: valorNoche, monto_bruto: subTotal, comisiones: comision });
  };

  const handleSubTotalChange = (value) => {
    const subTotal = parseFloat(value) || 0;
    const noches = calcularNoches(formData.fecha_check_in, formData.fecha_check_out);
    const valorNoche = noches > 0 ? Math.round(subTotal / noches) : 0;
    const comision = calcularComision(subTotal, formData.canal_id);
    setFormData({ ...formData, monto_bruto: subTotal, valor_noche: valorNoche, comisiones: comision });
  };

  const handleCanalChange = (value) => {
    const comision = calcularComision(formData.monto_bruto, value);
    setFormData({ ...formData, canal_id: value, comisiones: comision });
  };

  // No need to recalculate on gastosReserva changes anymore since Sub Total doesn't include gastos

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {reservaEditando ? 'Editar Reserva' : 'Nueva Reserva'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Selector de Cliente - Always visible */}
          <div className="bg-[#F7E8C9]/30 p-4 rounded-xl">
            <Label className="text-[#0E3A53] font-semibold mb-2 block">
              {reservaEditando ? 'Cambiar Cliente (opcional)' : 'Cliente *'}
            </Label>
            <div className="flex gap-2">
              <Select value={clienteSeleccionado} onValueChange={onSeleccionarCliente}>
                <SelectTrigger className="flex-1" data-testid="select-client">
                  <SelectValue placeholder={reservaEditando ? "Buscar cliente para actualizar datos..." : "Seleccionar cliente existente..."} />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map(cliente => (
                    <SelectItem key={cliente.id_cliente} value={cliente.id_cliente}>
                      {cliente.nombre} {cliente.apellido || ''} {cliente.telefono ? `- ${cliente.telefono}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                type="button" 
                variant="outline" 
                onClick={onAbrirNuevoCliente}
                className="border-[#1AA1D8] text-[#1AA1D8]"
                data-testid="btn-new-client"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nuevo
              </Button>
            </div>
            {reservaEditando && (
              <p className="text-xs text-[#0E3A53]/50 mt-2">
                Selecciona un cliente para cargar sus datos en la reserva
              </p>
            )}
          </div>

          {/* Datos del huésped */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="huesped_nombre">Nombre del Huésped</Label>
              <Input
                id="huesped_nombre"
                value={formData.huesped_nombre}
                onChange={(e) => setFormData({ ...formData, huesped_nombre: e.target.value })}
                disabled={!reservaEditando && !!clienteSeleccionado}
                data-testid="input-guest-name"
              />
            </div>
            <div>
              <Label htmlFor="huesped_telefono">Teléfono</Label>
              <Input
                id="huesped_telefono"
                value={formData.huesped_telefono}
                onChange={(e) => setFormData({ ...formData, huesped_telefono: e.target.value })}
                disabled={!reservaEditando && !!clienteSeleccionado}
              />
            </div>
            <div>
              <Label htmlFor="huesped_email">Email</Label>
              <Input
                id="huesped_email"
                type="email"
                value={formData.huesped_email}
                onChange={(e) => setFormData({ ...formData, huesped_email: e.target.value })}
                disabled={!reservaEditando && !!clienteSeleccionado}
              />
            </div>
          </div>

          {/* Fechas y Canal */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="fecha_check_in">Check-in</Label>
              <Input
                id="fecha_check_in"
                type="date"
                value={formData.fecha_check_in}
                onChange={(e) => handleCheckInChange(e.target.value)}
                data-testid="input-checkin"
              />
            </div>
            <div>
              <Label htmlFor="fecha_check_out">Check-out</Label>
              <Input
                id="fecha_check_out"
                type="date"
                value={formData.fecha_check_out}
                onChange={(e) => handleCheckOutChange(e.target.value)}
                data-testid="input-checkout"
              />
            </div>
            <div>
              <Label htmlFor="canal_id">Canal</Label>
              <Select value={formData.canal_id} onValueChange={handleCanalChange}>
                <SelectTrigger data-testid="select-channel">
                  <SelectValue placeholder="Seleccionar canal" />
                </SelectTrigger>
                <SelectContent>
                  {canales.map(canal => (
                    <SelectItem key={canal.id_canal} value={canal.id_canal}>
                      {canal.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="estado_reserva">Estado</Label>
              <Select 
                value={formData.estado_reserva} 
                onValueChange={(value) => setFormData({ ...formData, estado_reserva: value })}
              >
                <SelectTrigger data-testid="select-status">
                  <SelectValue placeholder="Seleccionar estado" />
                </SelectTrigger>
                <SelectContent>
                  {estadosReserva.map(estado => (
                    <SelectItem key={estado.id_estado} value={estado.nombre}>
                      {estado.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Montos */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <Label htmlFor="valor_noche">Valor por Noche</Label>
              <Input
                id="valor_noche"
                type="number"
                value={formData.valor_noche || ''}
                onChange={(e) => handleValorNocheChange(e.target.value)}
                className="text-right"
                placeholder="Ingrese valor"
                data-testid="input-price-per-night"
              />
            </div>
            <div>
              <Label>Noches</Label>
              <Input
                value={calcularNoches(formData.fecha_check_in, formData.fecha_check_out) || ''}
                disabled
                className="text-right font-semibold bg-gray-50"
                placeholder="-"
              />
            </div>
            <div>
              <Label htmlFor="sub_total">Sub Total</Label>
              <Input
                id="sub_total"
                type="number"
                value={formData.monto_bruto || ''}
                onChange={(e) => handleSubTotalChange(e.target.value)}
                className="text-right"
                placeholder="Ingrese monto"
                data-testid="input-amount"
              />
              {formData.valor_noche > 0 && (
                <p className="text-xs text-[#0E3A53]/50 mt-1">
                  {calcularNoches(formData.fecha_check_in, formData.fecha_check_out)} noches × ${formData.valor_noche?.toLocaleString('es-CL')}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="comisiones">Comisiones</Label>
              <Input
                id="comisiones"
                type="number"
                value={formData.comisiones || ''}
                onChange={(e) => setFormData({ ...formData, comisiones: parseFloat(e.target.value) || 0 })}
                className="text-right"
                placeholder="Ingrese comisión"
              />
            </div>
            <div>
              <Label>Monto Total</Label>
              <Input
                value={`$${((formData.monto_bruto || 0) + calcularTotalGastos()).toLocaleString('es-CL')}`}
                disabled
                className="text-right font-semibold text-[#1AA1D8] bg-[#8FE0FF]/10"
              />
              {calcularTotalGastos() > 0 && (
                <p className="text-xs text-[#0E3A53]/50 mt-1">
                  Sub total + ${calcularTotalGastos().toLocaleString('es-CL')} gastos
                </p>
              )}
            </div>
          </div>

          {/* Notas */}
          <div>
            <Label htmlFor="notas">Notas</Label>
            <Textarea
              id="notas"
              value={formData.notas}
              onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
              placeholder="Notas adicionales sobre la reserva..."
              rows={3}
            />
          </div>

          {/* Gastos asociados */}
          <div className="bg-[#8FE0FF]/10 p-4 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-[#FF7F6A]" />
                <Label className="text-[#0E3A53] font-semibold">Gastos Asociados</Label>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onAgregarGasto}
                className="border-[#FF7F6A] text-[#FF7F6A]"
              >
                <Plus className="w-4 h-4 mr-1" />
                Agregar Gasto
              </Button>
            </div>
            
            {gastosReserva.length > 0 ? (
              <div className="space-y-3">
                {gastosReserva.map((gasto, index) => (
                  <div key={gasto.id_gasto || gasto.id_temp} className="flex gap-2 items-start bg-white p-3 rounded-lg">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-2">
                      <Select
                        value={gasto.categoria_id}
                        onValueChange={(value) => onActualizarGasto(index, 'categoria_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Categoría" />
                        </SelectTrigger>
                        <SelectContent>
                          {categorias.map(cat => (
                            <SelectItem key={cat.id_categoria} value={cat.id_categoria}>
                              {cat.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <Select
                        value={gasto.proveedor_id || '__none__'}
                        onValueChange={(value) => onActualizarGasto(index, 'proveedor_id', value === '__none__' ? '' : value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Proveedor" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Sin proveedor</SelectItem>
                          {obtenerProveedoresPorCategoria(gasto.categoria_id).map(prov => (
                            <SelectItem key={prov.id_proveedor} value={prov.id_proveedor}>
                              {prov.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <Input
                        type="number"
                        placeholder="Monto"
                        value={gasto.monto || ''}
                        onChange={(e) => onActualizarGasto(index, 'monto', parseFloat(e.target.value) || 0)}
                        className="text-right"
                      />
                      
                      <Input
                        placeholder="Notas"
                        value={gasto.notas || ''}
                        onChange={(e) => onActualizarGasto(index, 'notas', e.target.value)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onEliminarGasto(index)}
                      className="text-[#FF7F6A]"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#0E3A53]/50 text-center py-2">
                No hay gastos asociados. Agrega gastos como limpieza, servicios, etc.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} data-testid="cancel-btn">
            Cancelar
          </Button>
          <Button 
            onClick={onGuardar}
            className="bg-[#1AA1D8] hover:bg-[#1AA1D8]/90"
            data-testid="save-btn"
          >
            {reservaEditando ? 'Guardar Cambios' : 'Crear Reserva'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReservaForm;
